import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../utils/api';

type Mode = 'LEGACY_ONLY' | 'DUAL_ADMIN' | 'V2_PRIMARY' | 'V2_ONLY';

type SettingsPayload = {
  success: boolean;
  data: {
    key: string;
    value: Mode;
    defaultValue: Mode;
    description?: string | null;
    updatedAt?: string | null;
    updatedByUser?: { id: string; name: string; email: string } | null;
  };
};

type AuditPayload = {
  success: boolean;
  data: Array<{
    id: string;
    key: string;
    previousValue: string | null;
    newValue: string;
    reason: string | null;
    createdAt: string;
    actorUser?: { id: string; name: string; email: string } | null;
  }>;
};

const MODES: Array<{ value: Mode; label: string; description: string }> = [
  { value: 'LEGACY_ONLY', label: 'LEGACY_ONLY', description: 'Только текущий legacy сценарий. Без V2 кнопок.' },
  { value: 'DUAL_ADMIN', label: 'DUAL_ADMIN', description: 'Legacy для всех + тестовая кнопка V2 только для админов.' },
  { value: 'V2_PRIMARY', label: 'V2_PRIMARY', description: 'Игрокам V2 как основной; у админа остаётся fallback на legacy.' },
  { value: 'V2_ONLY', label: 'V2_ONLY', description: 'Только V2. Legacy кнопки скрываются.' }
];

const formatDateTime = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU');
};

const MatchScoreEntrySettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reason, setReason] = useState('');
  const [currentValue, setCurrentValue] = useState<Mode>('LEGACY_ONLY');
  const [selectedValue, setSelectedValue] = useState<Mode>('LEGACY_ONLY');
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [updatedBy, setUpdatedBy] = useState<{ id: string; name: string; email: string } | null>(null);
  const [auditRows, setAuditRows] = useState<AuditPayload['data']>([]);

  const hasChanges = useMemo(() => selectedValue !== currentValue, [selectedValue, currentValue]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [settings, audit] = await Promise.all([
        apiGet<SettingsPayload>('/match-score-entry-settings'),
        apiGet<AuditPayload>('/match-score-entry-settings/audit?limit=50')
      ]);
      const value = settings?.data?.value || 'LEGACY_ONLY';
      setCurrentValue(value);
      setSelectedValue(value);
      setUpdatedAt(settings?.data?.updatedAt || null);
      setUpdatedBy(settings?.data?.updatedByUser || null);
      setAuditRows(Array.isArray(audit?.data) ? audit.data : []);
    } catch (e: any) {
      setError(e?.message || 'Не удалось загрузить настройки score entry V2');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const ensureReason = (): string | null => {
    const value = reason.trim();
    if (!value) {
      setError('Причина изменения обязательна');
      return null;
    }
    return value;
  };

  const save = async () => {
    setError('');
    setSuccess('');
    if (!hasChanges) {
      setSuccess('Изменений нет');
      return;
    }
    const reasonValue = ensureReason();
    if (!reasonValue) return;
    if (selectedValue === 'V2_ONLY') {
      const ok = window.confirm('Режим V2_ONLY полностью отключит legacy flow. Продолжить?');
      if (!ok) return;
    }
    setSaving(true);
    try {
      const payload = await apiPut<SettingsPayload>('/match-score-entry-settings', {
        value: selectedValue,
        reason: reasonValue
      });
      const next = payload?.data?.value || selectedValue;
      setCurrentValue(next);
      setSelectedValue(next);
      setUpdatedAt(payload?.data?.updatedAt || null);
      setUpdatedBy(payload?.data?.updatedByUser || null);
      setReason('');
      setSuccess('Режим score entry обновлён');
      const audit = await apiGet<AuditPayload>('/match-score-entry-settings/audit?limit=50');
      setAuditRows(Array.isArray(audit?.data) ? audit.data : []);
    } catch (e: any) {
      setError(e?.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    const reasonValue = ensureReason();
    if (!reasonValue) return;
    if (!window.confirm('Сбросить режим к fail-safe default (LEGACY_ONLY)?')) return;
    setResetting(true);
    setError('');
    setSuccess('');
    try {
      const payload = await apiPost<SettingsPayload>('/match-score-entry-settings/reset-defaults', {
        reason: reasonValue
      });
      const next = payload?.data?.value || 'LEGACY_ONLY';
      setCurrentValue(next);
      setSelectedValue(next);
      setUpdatedAt(payload?.data?.updatedAt || null);
      setUpdatedBy(payload?.data?.updatedByUser || null);
      setReason('');
      setSuccess('Режим сброшен к LEGACY_ONLY');
      const audit = await apiGet<AuditPayload>('/match-score-entry-settings/audit?limit=50');
      setAuditRows(Array.isArray(audit?.data) ? audit.data : []);
    } catch (e: any) {
      setError(e?.message || 'Ошибка сброса');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Score Entry V2 Rollout</h2>
        <p className="text-sm text-gray-600 mt-1">
          Управление режимом Telegram-ввода счёта. Fail-safe default: <b>LEGACY_ONLY</b>.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {MODES.map((mode) => (
            <label key={mode.value} className={`border rounded p-3 cursor-pointer ${selectedValue === mode.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  name="matchScoreEntryUiMode"
                  value={mode.value}
                  checked={selectedValue === mode.value}
                  onChange={() => setSelectedValue(mode.value)}
                />
                <span className="font-medium">{mode.label}</span>
              </div>
              <div className="text-xs text-gray-600 mt-2">{mode.description}</div>
            </label>
          ))}
        </div>

        <div>
          <label className="block text-sm text-gray-700 mb-1">Причина изменения (audit)</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full border rounded px-3 py-2"
            placeholder="Например: DEV rollout phase 1"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={save}
            disabled={saving || loading || !hasChanges}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
          <button
            onClick={resetDefaults}
            disabled={resetting || loading}
            className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
          >
            {resetting ? 'Сброс...' : 'Сбросить к LEGACY_ONLY'}
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="px-4 py-2 border rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Обновить
          </button>
        </div>

        <div className="text-sm text-gray-700">
          <div>Текущий режим: <b>{currentValue}</b></div>
          <div>Обновлено: {formatDateTime(updatedAt)}</div>
          <div>Кем: {updatedBy?.name || updatedBy?.email || '—'}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-auto">
        <div className="px-4 py-3 border-b text-sm font-medium text-gray-800">История изменений (audit)</div>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-600">
              <th className="px-3 py-2 text-left">Дата</th>
              <th className="px-3 py-2 text-left">Кто</th>
              <th className="px-3 py-2 text-left">Было</th>
              <th className="px-3 py-2 text-left">Стало</th>
              <th className="px-3 py-2 text-left">Причина</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td className="px-3 py-3 text-gray-500" colSpan={5}>Загрузка...</td>
              </tr>
            )}
            {!loading && auditRows.length === 0 && (
              <tr>
                <td className="px-3 py-3 text-gray-500" colSpan={5}>История пуста</td>
              </tr>
            )}
            {!loading && auditRows.map((row) => (
              <tr key={row.id} className="border-t">
                <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(row.createdAt)}</td>
                <td className="px-3 py-2">{row.actorUser?.name || row.actorUser?.email || '—'}</td>
                <td className="px-3 py-2">{row.previousValue || '—'}</td>
                <td className="px-3 py-2">{row.newValue}</td>
                <td className="px-3 py-2">{row.reason || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
      {success && <div className="text-sm text-green-700">{success}</div>}
    </div>
  );
};

export default MatchScoreEntrySettings;
